<template>
    <section class="py-20 md:px-6 lg:px-12" :class="backgroundColor">
        <div class="container mx-auto">
            <!-- Title & Subtitle -->
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold lg:text-center" :class="titleColor">
                <span v-html="renderTitle()"></span>
            </h2>
            <p class="text-[18px] mt-4 lg:text-center font-pro" :class="subtitleColor">
                {{ subtitle }}
            </p>

            <!-- Two Column Layout -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-10 items-center">
                <!-- Left Column: Accordion -->
                <div class="flex flex-col gap-3">
                    <div
                        v-for="(item, index) in localAccordionItems"
                        :key="index"
                        class="border rounded-xl p-5 transition-all cursor-pointer shadow-lg"
                        :style="{ borderColor: accordionBorderColor }"
                        @click="toggleAccordion(index)"
                    >
                        <!-- Header -->
                        <div class="flex justify-between items-center">
                            <h3 class="font-semibold text-sm md:text-base" :style="{ color: item.isOpen ? activeAccordionColor : accordionTextColor }">
                                {{ item.question }}
                            </h3>
                            <!-- Chevron Icon -->
                            <svg
                                class="w-5 h-5 transition-transform"
                                :class="{ 'rotate-180': item.isOpen }"
                                :style="{ color: item.isOpen ? activeAccordionColor : accordionTextColor }"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>

                        <!-- Body (shown when open) -->
                        <div v-if="item.isOpen" class="text-sm leading-relaxed mt-3 font-pro" :style="{ color: accordionBodyColor }">
                            <div v-html="formatAnswer(item.answer)"></div>
                        </div>
                    </div>
                </div>

                <!-- Right Column: Image -->
                <div class="rounded-2xl overflow-hidden w-full h-full min-h-[300px] lg:max-h-[600px]">
                    <img v-if="image" :src="image" :alt="imageAlt" class="w-full h-full object-fit" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
    backgroundColor: {
        type: String,
        default: '#ffffff'
    },
    title: {
        type: String,
        required: true
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightColor: {
        type: String,
        default: '#0d9488'
    },
    titleColor: {
        type: String,
        default: '#1a1a1a'
    },
    subtitle: {
        type: String,
        default: ''
    },
    subtitleColor: {
        type: String,
        default: '#000000'
    },
    accordionItems: {
        type: Array,
        required: true
    },
    accordionBorderColor: {
        type: String,
        default: '#e5e7eb'
    },
    accordionTextColor: {
        type: String,
        default: '#1a1a1a'
    },
    accordionBodyColor: {
        type: String,
        default: '#4b5563'
    },
    activeAccordionColor: {
        type: String,
        default: '#0d9488'
    },
    image: {
        type: String,
        default: ''
    },
    imageAlt: {
        type: String,
        default: 'Analysis Image'
    }
});

// Create local reactive copy of accordion items
const localAccordionItems = ref([...props.accordionItems]);

// Watch for prop changes
watch(
    () => props.accordionItems,
    (newItems) => {
        localAccordionItems.value = [...newItems];
    },
    { deep: true }
);

const toggleAccordion = (index) => {
    localAccordionItems.value[index].isOpen = !localAccordionItems.value[index].isOpen;
};

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    return parts
        .map((part, index) => {
            if (index === 0) {
                return part;
            }

            return `<span style="color: ${props.titleHighlightColor}">${props.titleHighlight}</span>${part}`;
        })
        .join('');
};

const formatAnswer = (answer) => {
    // Replace bullet points with proper HTML list items
    return answer
        .split('\n')
        .map((line) => {
            line = line.trim();

            if (line.startsWith('•')) {
                return `<div class="pl-4 flex gap-2"><span>•</span><span>${line.substring(1).trim()}</span></div>`;
            }

            return line ? `<p>${line}</p>` : '';
        })
        .join('');
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
